import { GAMMA_HOST } from './constants';
import { GAMMA_FETCH_TIMEOUT_MS } from './fetchGammaEvent.js';

/** @type {Map<string, object[]>} */
const cache = new Map();

/** @type {Map<string, Promise<object[]>>} */
const inflight = new Map();

function normalizeSlugs(slugs) {
  return [...new Set(slugs.map((s) => String(s ?? '').trim()).filter(Boolean))];
}

function cacheKey(slugs) {
  return normalizeSlugs(slugs)
    .map((s) => s.toLowerCase())
    .sort()
    .join('|');
}

/**
 * PM 批量 markets：/markets?limit=N&slug=a&slug=b
 * @param {string[]} slugs
 * @returns {Promise<object[]>}
 */
async function fetchGammaMarketsRaw(slugs) {
  const list = normalizeSlugs(slugs);
  if (!list.length) return [];

  const q = new URLSearchParams({ limit: String(Math.max(list.length, 1)) });
  for (const slug of list) {
    q.append('slug', slug);
  }

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), GAMMA_FETCH_TIMEOUT_MS);

  try {
    const res = await fetch(`${GAMMA_HOST}/markets?${q.toString()}`, {
      signal: controller.signal,
    });
    if (!res.ok) {
      console.warn(`[gamma-markets] HTTP ${res.status} slugs=${list.length}`);
      return [];
    }

    const data = await res.json();
    return Array.isArray(data) ? data : [];
  } catch (err) {
    const reason = err?.name === 'AbortError' ? 'timeout' : err?.message || err;
    console.warn('[gamma-markets] fetch failed', reason);
    return [];
  } finally {
    clearTimeout(timer);
  }
}

/**
 * @param {string[]} slugs
 * @returns {Promise<object[]>}
 */
export async function fetchGammaMarketsBySlugs(slugs) {
  const key = cacheKey(slugs);
  if (!key) return [];

  if (cache.has(key)) {
    return cache.get(key) ?? [];
  }

  const pending = inflight.get(key);
  if (pending) {
    return pending;
  }

  const promise = fetchGammaMarketsRaw(slugs)
    .then((rows) => {
      cache.set(key, rows);
      return rows;
    })
    .catch((err) => {
      console.warn('[gamma-markets] cached fetch failed', err);
      cache.set(key, []);
      return [];
    })
    .finally(() => {
      inflight.delete(key);
    });

  inflight.set(key, promise);
  return promise;
}

/**
 * @param {string} slug
 * @returns {Promise<object|null>}
 */
export async function fetchGammaMarketBySlug(slug) {
  const s = String(slug ?? '').trim();
  if (!s) return null;

  const rows = await fetchGammaMarketsBySlugs([s]);
  const needle = s.toLowerCase();
  return rows.find((row) => String(row?.slug ?? '').trim().toLowerCase() === needle) ?? rows[0] ?? null;
}

/** @param {string} slug */
export function getCachedGammaMarket(slug) {
  const s = String(slug ?? '').trim().toLowerCase();
  if (!s) return undefined;

  for (const rows of cache.values()) {
    const hit = rows.find((row) => String(row?.slug ?? '').trim().toLowerCase() === s);
    if (hit) return hit;
  }
  return undefined;
}
